<template>
    <v-container class="main" fluid :id="`${baseId}`">
        <v-container class="content dashboard_main_min_max_width" pa-0 :id="`${baseId}-container`">
            <!-- Heading -->
            <v-layout class="dashboard_header" :id="`${baseId}-header-container`">
                <v-flex sm12 :id="`${baseId}-header-flex`">
                    <v-layout class="wrap_header" :id="`${baseId}-header-flex-wrap`">
                        <div class="product_logo mx-0 px-5" :id="`${baseId}-header-page-logo-container`">
                            <img :src="pageLogo" :id="`${baseId}-header-page-logo-image`"/>
                        </div>
                        <div class="product_learn_more learn_more_mobile" :id="`${baseId}-header-learn-more-mobile`">
                            <a
                            :href="landingPageLink" @click="publishEvent('loyaltyLearn', { context: 'dashboard' })"
                            :id="`${baseId}-header-mobile-learn-more-link`"
                            >{{ $nsLanguage.getTranslation('learn_more_about_name', { name: $nsLanguage.getTranslation('product') }) }}</a>
                        </div>
                        <div class="wallet" :id="`${baseId}-header-wallet-container`">
                            <div class="wallet_title" :id="`${baseId}-header-wallet-title`">{{ $nsLanguage.getTranslation('wallet') }}</div>
                            <div class="wallet-stack" :id="`${baseId}-header-wallet-stack`">
                                <div class="wallet_value" :id="`${baseId}-header-wallet-value`">{{ getCurrency(walletValue) }}</div>
                                <div class="p-small wallet_branding" :id="`${baseId}-header-wallet-branding`">{{ $nsLanguage.getTranslation('dashboard.headerWallet.enjoyCreditsLabel') }}</div>
                            </div>
                        </div>
                        <div
                            class="wallet_soonest_expiring" :class="[soonestExpiring.length > 0 ? 'wallet-default' : 'wallet-hidden']"
                            :id="`${baseId}-header-wallet-soonest-expiring-container`"
                        >
                            <RewardsStatus
                                :rewardsValues="soonestExpiring"
                                :showTierExpiration="false"
                                :shortView="true"
                                v-if="soonestExpiring.length > 0"
                                :parent-id="`${baseId}-header-wallet-soonest-expiring`"
                            />
                            <div class="product_learn_more learn_more_desktop" :id="`${baseId}-header-learn-more-non-mobile`">
                                <a
                                    class="learn_more_link"
                                    :href="landingPageLink"
                                    @click="publishEvent('loyaltyLearn', { context: 'dashboard' })"
                                    :id="`${baseId}-header-non-mobile-learn-more-link`"
                                >{{ $nsLanguage.getTranslation('learn_more_about_name', { name: $nsLanguage.getTranslation('product') }) }}</a>
                            </div>
                        </div>
                    </v-layout>
                </v-flex>
            </v-layout>
            <!-- Tiering -->
            <v-layout v-if="showTiering" class="section-tiering section-padding" justify-center pa-5 pt-0 sm12 :id="`${baseId}-tiering-container-layout`">
                <v-flex xs12 sm12 md10 lg12 :id="`${baseId}-tiering-container-flex`">
                    <BrushstrokeHeading class="bs-heading" :brushstroke-color="brushstrokeColor" :parent-id="`${baseId}-tiering`">
                        <template v-slot:title>
                            <span
                                class="bs-text tx-font-primary"
                                v-html="$nsLanguage.getTranslation('rewards_progress')"
                                :id="`${baseId}-tiering-brushstroke-heading-rewards-progress-title`"
                            >
                            </span>
                        </template>
                    </BrushstrokeHeading>
                    <div
                        class="tiering ma-0 mx-auto"
                        :class="{
                            'mobile': $vuetify.breakpoint.smAndDown,
                            'tablet': $vuetify.breakpoint.md,
                            'desktop': $vuetify.breakpoint.lgAndUp
                        }"
                        :id="`${baseId}-tiering-container`"
                    >
                        <template v-if="tierGroupView == 'group'">
                            <div class="tiering-container" :id="`${baseId}-tiering-container-group`">
                                <ns-tier-group-card
                                    ref="tiering-group"
                                    class="tier-card"
                                    :layout="layout"
                                    :links="tieringLinks"
                                    :togglable="false"
                                    :parent-id="`${baseId}-tiering-container-group`"
                                />
                            </div>
                        </template>
                        <template v-if="tierGroupView == 'cards'">
                            <div class="tiering-container" :id="`${baseId}-tiering-container-cards`">
                                <ns-white-tier-card
                                    ref="tiering-cards"
                                    :links="tieringLinks"
                                    :parent-id="`${baseId}-tiering-container-cards`"
                                />
                            </div>
                        </template>
                    </div>
                </v-flex>
            </v-layout>
            <div class="section-foot tx-font-primary" :id="`${baseId}-tiering-questions-section`">
                <p :id="`${baseId}-tiering-questions-paragraph`">
                    <span :id="`${baseId}-tiering-questions-text`">{{ $nsLanguage.getTranslation('still_have_questions') }} </span>
                    <br class="line-break" :id="`${baseId}-tiering-questions-line-break`"/>
                    <a :href="faqPage" @click="publishEvent('loyaltyLearn', { context: 'dashboard' })" :id="`${baseId}-tiering-questions-link`"
                    >{{ $nsLanguage.getTranslation('learn_more_about_product_rewards') }}</a>
                </p>
            </div>
            <!-- Wallet -->
            <v-layout class="section-wallet section-padding background-color-gray" pa-5 pt-0 xs12 :id="`${baseId}-wallet-container`">
                <v-flex xs12 md10 offset-md1 lg10 offset-lg1 :id="`${baseId}-wallet-flex`">
                    <BrushstrokeHeading class="bs-heading" :brushstroke-color="brushstrokeColor" :parent-id="`${baseId}-wallet`">
                        <template v-slot:title>
                            <span class="bs-text tx-font-primary" v-html="$nsLanguage.getTranslation('product_wallet')" :id="`${baseId}-wallet-brushstroke-heading-product-wallet`">
                            </span>
                        </template>
                        <template v-slot:text>
                            <span
                                class="bs-subtext tx-font-primary"
                                v-html="$nsLanguage.getTranslation('redeem_during_checkout')"
                                :id="`${baseId}-wallet-brushstroke-heading-redeem-during-checkout-text`"
                            >
                            </span>
                        </template>
                    </BrushstrokeHeading>
                    <v-layout
                        class="rewards_status_section_total"
                        :id="`${baseId}-wallet-total-rewards-container`"
                    >
                        <div class="total_text" :id="`${baseId}-wallet-total-rewards-total`">{{ $nsLanguage.getTranslation('total') }}</div>
                        <div class="total-stack" :id="`${baseId}-wallet-total-rewards-stack`">
                            <div class="total_value" :id="`${baseId}-wallet-total-rewards-value`">{{ getCurrency(walletValue) }}</div>
                            <div class="p-small total_branding" :id="`${baseId}-wallet-total-rewards-branding`">{{ $nsLanguage.getTranslation('dashboard.total.enjoyCreditsLabel') }}</div>
                        </div>
                    </v-layout>
                    <RewardsStatus
                                :rewardsValues="soonestExpiring"
                                :showTierExpiration="false"
                                :shortView="true"
                                v-if="soonestExpiring.length > 0"
                                :parent-id="`${baseId}-header-wallet-soonest-expiring`"
                            />
                </v-flex>
            </v-layout>
            <!-- History -->
            <v-layout class="section-history" sm12 pa-5 pt-0 :id="`${baseId}-history-container`">
                <v-flex xs12 md10 offset-md1 :id="`${baseId}-history-flex`">
                    <BrushstrokeHeading class="bs-heading" :brushstroke-color="brushstrokeColor" :parent-id="`${baseId}-history`">
                        <template v-slot:title><span id="history_header" class="bs-text tx-font-primary" v-html="$nsLanguage.getTranslation('rewards_history')"></span></template>
                    </BrushstrokeHeading>
                    <RewardsHistory context="dashboard" :creditsHistory="creditsHistory" :lifetimeSpend="lifetimeSpend" :parent-id="`${baseId}-history`"/>
                </v-flex>
            </v-layout>
            <v-layout class="section-padding" sm12 pa-5 pb-15 :id="`${baseId}-optout-container`">
                <v-flex xs12 md10 offset-md1 :id="`${baseId}-optout-flex`">
                    <div class="opt_out_link" :id="`${baseId}-optout-link-container`">
                        <div class="opt_out_text" @click="optOutDialog = true" :id="`${baseId}-optout-text`">{{ $nsLanguage.getTranslation('opt_out') }}</div>
                    </div>
                    <v-dialog
                        v-model="optOutDialog"
                        max-width="600"
                        content-class="opt-out-dialog"
                        @click:outside="closeOptOut('loyaltyOptoutOutsideCancel')" :id="`${baseId}-optout-dialog`"
                    >
                        <v-card class="opt_out_card" :id="`${baseId}-optout-dialog-card`">
                            <v-card-text :id="`${baseId}-optout-dialog-card-text`">
                                <div class="opt_out_header title-small-bold" :id="`${baseId}-optout-dialog-card-text-title`">
                                    {{ $nsLanguage.getTranslation('opt_out') }}
                                </div>
                                <div
                                    class="opt_out_warning tx-font-primary tx-em-bold-800"
                                    v-html="$nsLanguage.getTranslation('opt_out_warning')"
                                    v-if="walletValue > 0"
                                    :id="`${baseId}-optout-dialog-card-text-warning`"
                                >
                                </div>
                                <div v-if="optOutFail" class="opt_out_fail tx-font-primary" :id="`${baseId}-optout-dialog-card-opt-out-fail`">
                                    {{ $nsLanguage.getTranslation('opt_out_fail') }}
                                </div>
                            </v-card-text>
                            <v-card-actions class="opt_out_dialog_buttons" :id="`${baseId}-optout-dialog-card-buttons`">
                                <div class="button-container" :id="`${baseId}-optout-dialog-card-buttons-container`">
                                    <button class="primary-solid fluid button-accept title-small-bold" @click="optUserOut()" :id="`${baseId}-optout-dialog-card-buttons-opt-out`">
                                        <span :id="`${baseId}-optout-dialog-card-buttons-opt-out-text`">{{ $nsLanguage.getTranslation('confirm') }}</span>
                                    </button>
                                    <button
                                        class="primary-ghost fluid button-cancel title-small-bold"
                                        @click="closeOptOut('loyaltyOptoutCancel')"
                                        :id="`${baseId}-optout-dialog-card-buttons-cancel`"
                                    >
                                        <span :id="`${baseId}-optout-dialog-card-buttons-cancel-text`">{{ $nsLanguage.getTranslation('cancel') }}</span>
                                    </button>
                                </div>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-flex>
            </v-layout>
        </v-container>
        <NsSpinner v-if="busy"></NsSpinner>
    </v-container>
</template>

<script>

import Vue from 'vue'

import { ConfigService, RunConfigService, events } from '@nuskin/ns-util'
import { UserService } from '@nuskin/ns-account'
import webLoyalty from '@nuskin/ns-loyalty-web'

import NsPublishEvent from '@/mixin/publish-event.vue'
import NsSpinner from '@/components/common/ns-spinner-color-circle.vue'
import BrushstrokeHeading from '@/components/common/BrushstrokeHeading.vue'
import RewardsStatus from '@/components/RewardsStatus.vue'
import RewardsHistory from '@/components/RewardsHistory.vue'
import NsDashboardTierGroupCard from '@/components/Tiering/Dashboard/DashboardTierGroupCard.vue'
import NsDashboardWhiteTierCard from '@/components/Tiering/Dashboard/DashboardWhiteTieringCards.vue'
import TieringMixin from '@/components/Tiering/TieringMixin.vue'

import { formatPoints } from '../services/points'

import ReplaceLocale from '@/services/replace_locale.js'

export default {
    name: 'Dashboard',
    components: {
        NsSpinner,
        BrushstrokeHeading,
        RewardsHistory,
        RewardsStatus,
        'ns-tier-group-card': NsDashboardTierGroupCard,
        'ns-white-tier-card': NsDashboardWhiteTierCard
    },
    mixins: [
        NsPublishEvent,
        TieringMixin
    ],
    computed: {
        isMobile () {
            return this.windowWidth < 960
        },
        isTablet () {
            return this.windowWidth >= 960 && this.windowWidth < 1264
        },
        layout () {
            let layout

            if (this.isMobile) {
                layout = 'mobile'
            } else if (this.isTablet) {
                layout = 'tablet'
            } else {
                layout = 'desktop'
            }

            return layout
        }
    },
    data () {
        return {
            baseId: 'loyalty-app-dashboard',
            busy: true,
            aemText: {},
            brushstrokeColor: '#02B2DF',
            creditsHistory: [],
            faqPage: '#',
            inMaintenanceMode: false,
            landingPageLink: '',
            language: '',
            lifetimeSpend: 0,
            market: '',
            optOutDialog: false,
            optOutFail: false,
            pageLogo: '',
            pointsAcquired: 0,
            soonestExpiring: [],
            showTiering: false,
            tierGroupView: 'group',
            tieringLinks: {},
            walletValue: 0,
            windowWidth: 0
        }
    },
    methods: {
        calculateDayDifference (date) {
            const now = new Date()
            const expireDate = new Date(date)
            return Math.floor(
                (Date.UTC(expireDate.getFullYear(), expireDate.getMonth(), expireDate.getDate()) - Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) /
                (1000 * 60 * 60 * 24)
            )
        },
        closeOptOut: function (eventName) {
            if (eventName) {
                events.tag(eventName, { context: 'dashboard' })
            }

            this.optOutDialog = false
            this.optOutFail = false
        },
        determineLongestExpirationFromUserTierAndActivity: function (loyaltyResponseData) {
            let farthestOut = 0
            loyaltyResponseData.activity.forEach((activityItem) => {
                const expiresIn = this.calculateDayDifference(activityItem.expireDate)
                if (expiresIn > farthestOut) {
                    farthestOut = expiresIn
                }
            })

            if (farthestOut > loyaltyResponseData.userTier.daysToExpire) {
                return farthestOut
            }

            return loyaltyResponseData.userTier.daysToExpire
        },
        getBrowserSafeDate: function (dateString) {
            let date = {}
            try {
                // Safari requires a "T" to separate the date and time.
                if (dateString.includes('T')) {
                    date = new Date(dateString)
                } else {
                    const parts = dateString.split(' ')
                    const newDateString = `${parts[0]}T${parts[1]}+0000`
                    date = new Date(newDateString)
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Problem parsing date string')
                date = new Date(dateString)
            }
            return date
        },
        getCurrency: function (value) {
            return formatPoints(value)
        },
        getRewardsData: async function () {
            try {
                const response = await webLoyalty.getAllLoyaltyInfo()
                if (response) {
                    if (response.optInStatus === false) {
                        window.location.replace(this.landingPageLink)
                        return
                    }

                    this.pointsAcquired = response.wallet.spentInPeriod
                    this.setSoonestExpiring(response.soonestExpiring)
                    this.walletValue = response.wallet.available
                    this.creditsHistory = response.activity
                    this.lifetimeSpend = response.wallet.spent
                    this.setupTiering(response)

                    // This happens on dashboard page load so that we are always in sync with the header and the output on this page.
                    events.publish(events.loyalty.UPDATE_LOYALTY_DATA)
                }
            } catch (ex) {
                if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console
                    console.error(ex)
                }
            } finally {
                this.busy = false
            }
        },
        optUserOut: function () {
            webLoyalty.loyaltyOptOut().then(response => {
                events.tag(events.loyalty.LOYALTY_OPTOUT_CONFIRM, { context: 'dashboard', success: !!response.success })

                if (response.success === true) {
                    this.optOutDialog = false
                    window.location.replace(this.landingPageLink)
                } else {
                    this.optOutFail = true
                }
            })
        },
        setSoonestExpiring: function (soonestExpiringObject) {
            const expireDate = this.getBrowserSafeDate(soonestExpiringObject.expirationDate)
            const expireDayCount = this.calculateDayDifference(expireDate)

            if (soonestExpiringObject.pointsToExpire) {
                this.soonestExpiring.push({
                    background: 'green',
                    credits: soonestExpiringObject.pointsToExpire,
                    expiration: this.$nsLanguage.getTranslation('expires_in_days', { number_of_days: expireDayCount }),
                    expiresDate: expireDate,
                    hideLabelOnTablet: true,
                    upperBanner: this.$nsLanguage.getTranslation('expires_soon')
                })
            }
        },
        setupTiering: function (data) {
            const entityTypes = {}
            entityTypes[data.entityType.toLowerCase().replace(/[^A-Za-z]/g, '')] = true

            const tierConfig = require('@/components/Tiering/Dashboard/dashboardTiering.json')
            const tieringFac = this.createTieringFactory()
            tieringFac.mergeConfiguration(tierConfig['*'])

            let marketTierConfig = tierConfig[this.market]
            if (!marketTierConfig) {
                marketTierConfig = tierConfig.default
            }
            tieringFac.mergeConfiguration(marketTierConfig)

            tieringFac.mergeTierData(data.tierData, 'name')
            tieringFac.mergeTierGroups(entityTypes)

            this.tieringLinks = {
                becomeVipMember: '#',
                optIntoOurSubscriptionProgram: ReplaceLocale.replaceLocale(this.$nsLanguage.getTranslation('subscriptionLink')),
                shopNow: ReplaceLocale.replaceLocale(this.$nsLanguage.getTranslation('shopLink'))
            }

            const activeTierName = data.userTier.name
            const tiering = tieringFac.build(activeTierName, this.pointsAcquired)[0]
            this.showTiering = !!tiering

            if (tiering) {
                this.tierGroupView = tiering.configuration.view

                Vue.nextTick(() => {
                    const tierComponent = this.$refs['tiering-' + this.tierGroupView]
                    if (tierComponent && (typeof tierComponent.setTiers) === 'function') {
                        if (tierComponent.setConfiguration) {
                            tierComponent.setConfiguration(tiering.configuration)
                        }

                        tierComponent.setTiers(tiering.tiers)
                        tierComponent.setActiveTier(activeTierName)
                    }
                })
            }
        },
        windowResize () {
            this.windowWidth = window.innerWidth
        }
    },
    async beforeMount () {
        try {
            if ((typeof loyaltyAuthorConfig) !== 'undefined') {
                // eslint-disable-next-line no-undef
                this.aemText = loyaltyAuthorConfig
            }

            const runConfig = RunConfigService.getRunConfig()
            const marketConfig = ConfigService.getMarketConfig()

            this.language = runConfig.language
            this.market = runConfig.country
            this.pageLogo = marketConfig.loyalty.dashboardPageLogo || '/content/dam/apps/loyalty/global/enJoy_nuskinrewards.svg'
            this.inMaintenanceMode = marketConfig.loyalty.inMaintenanceMode

            ReplaceLocale.initialize(this.language, this.market)
            this.landingPageLink = ReplaceLocale.replaceLocale(marketConfig.loyalty.landingPage)
            if ((typeof this.aemText.faq) !== 'undefined' && (typeof this.aemText.faq.faqPage) !== 'undefined') {
                this.faqPage = ReplaceLocale.replaceLocale(this.aemText.faq.faqPage) + '.html'
            }

            if (UserService.isLoggedIn() && !this.inMaintenanceMode) {
                const user = await UserService.getUser()
                // Check for out of market:
                if (this.market === user.countryCd) {
                    this.getRewardsData()
                } else {
                    window.location.replace(this.landingPageLink)
                }
            } else {
                window.location.replace(this.landingPageLink)
            }
        } catch (ex) {
        }

        window.addEventListener('resize', this.windowResize)
        this.windowResize()
    },
    destroyed () {
        window.removeEventListener('resize', this.windowResize)
    }
}

</script>

<style lang="scss" scoped>

@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";

$color-blackhole: map.get(colors.$baseColors, blackhole);
$color-dryink: map.get(colors.$baseColors, dryink);
$color-link-blue: #02B2DF;

$page-width-small: 550px;
$page-width-large: 964px;

.tx-em-bold-800 {
    ::v-deep {
        b, em, i {
            font-style: inherit;
            font-weight: 800;
        }
    }
}

.main {
    box-sizing: border-box;

    position: relative;
    min-width: 340px;
    min-height: 100%;
    background-image: url(~@/assets/stripes.svg), linear-gradient(#C4D400 0%, #00B5E2 100%);
    background-repeat: repeat;
}

.dashboard_header {
    padding-top: 15px;
    padding-bottom: 15px;
    border-bottom: 1px solid #CED0D0;
}

.dashboard_main_min_max_width {
    min-width: 350px;
    max-width: 964px;
}

.content {
    background: #FFFFFF;
    box-shadow: 0 2px 17px 3px rgba( 132,129,129,0.25 );
}

.product_logo {
    display: flex;
    align-items: center;
    max-width: 100%;
}

.product_logo img {
    max-width: 177px
}

.tiering {
    width: 100%;

    &.mobile {
        max-width: 400px;
    }

    &.tablet {
        max-width: 100%;
    }

    &.desktop {
        max-width: 100%;
    }
}

.wallet {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex-wrap: wrap;
    padding: 0px 16px;
}

.wallet-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.wallet_title {
    text-align: left;
    flex: 0 0 auto;
    width: auto;
    text-transform: uppercase;
    padding-top: 0px;
    color: #63666a;
}

.wallet_value {
    width: auto;
    text-align: left;
    flex: 0 0 auto;
    padding-top: 0px;
    font-size: 30px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
}

.wallet_branding {
    color: rgba(#000000, 85%);
}

.wallet_soonest_expiring {
    flex: 1;
    min-width: 344px;
    margin-right: 16px;
    margin-left: 16px;

    &.wallet-hidden {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        align-items: center;

        .product_learn_more {
            margin-top: 0;
        }
    }
}

.product_learn_more {
    text-align: right;
    font-size: 16px;
    color: #00B5E2;
    align-items: end;
    margin-top: 10px;

    a {
        color: inherit;
    }
}

.learn_more_mobile{
    display: none;
}

.opt_out_link, .learn_more_link{
    text-align: right;
    font-size: 16px;
    color: #00b5e2;
    text-decoration: underline;
    cursor: pointer;
}

.opt_out_link {
    cursor: inherit;
    text-align: right;

    .opt_out_text {
        cursor: pointer;
        display: inline-block;
        text-decoration: underline;
    }
}

.opt_out_card {
    border: 0px none;
    border-top: 4px solid #35B2F0;
    border-radius: 0px !important;
    color: #272525;
    min-height: 200px;
    display: flex;
    flex-wrap: wrap;
    justify-content: end;
}

.opt_out_warning, .opt_out_fail {
    margin-top: 15px;
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: 1.125em;
    text-align: left;
    color: #272525;
}

.opt_out_fail {
    color: #b2140a
}

.opt_out_dialog_buttons {
    width: 100%;
}

.opt_out_header {
    text-align: left;
    color: #272525;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3333em;
}

.opt_out_cancel {
    text-transform: uppercase;
    text-align: center;
    margin-right: 20px;
    font-size: 14px;
    font-weight: 600;
}

.button-container {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-content: center;
    align-items: center;
    width: 100%;

    .button-accept,
    .button-cancel {
        margin: 0;
    }

    .button-accept {
        order: 2;
        width: 250px;
        margin: 0 0 0 20px;
    }

    .button-cancel {
        order: 1;
        width: auto;
        height: auto;
        text-align: center;
        background: transparent;
        border: 1px solid transparent;
        color: $color-dryink;
    }
}

// View - Small
@media screen and (max-width: $page-width-small) {
    .button-container {
        flex-direction: column;
        margin: 30px 0 0;

        .button-accept,
        .button-cancel {
            width: 100%;
            margin: 10px 0 0;
        }

        .button-accept {
            order: 1;
        }

        .button-cancel {
            order: 2;
            background: transparent;
            border: 1px solid rgb(99, 102, 106);
            color: $color-dryink;
        }
    }
}

// View - Larger than Small
@media screen and (min-width: $page-width-small + 1) {
    .button-cancel {
        padding: 0;

        &:active {
            padding: 0;
        }
    }

    .opt_out_cancel:hover{
        color: #6236FF;
    }
    .product_logo {
        padding-left: 40px !important;
    }
}

.opt_out_cancel_mobile{
    display: none;
}

.opt_out_confirm{
    min-width: 170px;
    font-size: 14px;
    font-weight: 600;
}

.wrap_header {
    flex-direction: row;
    flex-wrap: wrap;
}

// View - Small
@media screen and (max-width: $page-width-small) {
    .wrap_header {
        flex-direction: column;
    }

    .wallet-stack {
        flex-direction: row;
        align-items: center;
    }

    .wallet_branding {
        margin-left: 10px;
    }

    .wallet_soonest_expiring {
        margin-top: 20px;
        min-width: 275px;
    }
    .product_logo{
        width: 100%;
        text-align: center;
        justify-content: center;
        padding: 16px 0px;
    }
    .product_logo img{
        width: 177px;
    }
    .learn_more_mobile {
        text-align: center;
        width: 100%;
        display: block;
        padding-bottom: 10px;
    }
    .learn_more_desktop{
        display: none;
    }

    .opt_out_dialog_buttons{
        flex-wrap: wrap;
        align-self: center;
        width: 100%;
    }

    .opt_out_cancel{
        flex-shrink: 0;
        display: none;
    }

    .opt_out_cancel_mobile{
        display: inline;
        margin-top: 12px;
    }

    .opt_out_confirm{
        flex-shrink: 0;
    }
}

// View - Large
@media screen and (min-width: $page-width-large) {
    .wallet_soonest_expiring {
        padding-right: 24px;
    }
}

.bs-heading {
    margin-top: 40px;
    margin-bottom: 40px;

    ::v-deep em {
        font-style: inherit;
        font-weight: 600;
    }

    .bs-text {
        color: $color-blackhole;
        font-size: 22px !important;
        font-weight: 400 !important;
    }

    .bs-subtext {
        color: $color-dryink;
        font-size: 16px;
        font-weight: normal;
        line-height: 1.1250em;
    }
}

.section-padding {
    padding-top: 0 !important;
    padding-bottom: 60px !important;
}

.section-foot {
    line-height: 18px;
    padding-bottom: 30px;
    color: $color-blackhole;
    font-size: 14px;
    font-weight: normal;
    text-align: center;

    a {
        display: inline-block;
        margin-top: 10px;
        color: $color-link-blue;
        text-decoration: underline;
    }

    p {
        font-size: inherit;
    }

    // View - Small
    @media screen and (max-width: $page-width-small) {
        .line-break {
            display: inherit;
        }
    }

    // View - Larger than Small
    @media screen and (min-width: $page-width-small + 1) {
        .line-break {
            display: none;
        }
    }
}

.section-tiering {
    &.section-padding {
        padding-bottom: 30px !important;
    }

    .section-container {
        width: 100%;
    }
}

.background-color-gray {
    background-color: #F7F8F8;
}

.rewards_status_section_total {
    font-size: 30px;
}

.total_text, .total_value {
    flex: 1;
}

.total_text {
    text-align: left;
    text-transform: uppercase;
}

.total-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.total_value {
    text-align: right;
    font-weight: 600;
}

.total_branding {
    color: rgba(0, 0, 0, 0.85);
    font-size: 14px;
    font-weight: normal;
    line-height: 1.2857em;
    text-align: right;
}

</style>

<style lang="scss">

// Vuetify addition
.v-application .pb-15 {
    padding-bottom: 60px !important;
}
// End of Vuetify addition

.opt-out-dialog {
    border-radius: 0px !important;
    border: thin solid #979797;
    border-top: 4px solid #35b2f0;
}

</style>
